import DOMPurify from "dompurify";

export const AI_DRAFT_STATE = {
  HIDDEN: "hidden",
  NO_KEY_IMAGES: "noKeyImages",
  IDLE: "idle",
  GENERATING: "generating",
  READY: "ready",
  VIEWED: "viewed",
  FAILED: "failed",
  STALE: "stale",
};

export const IN_FLIGHT_STATUSES = ["requested", "submitted"];

// ---- generations (one model each; the viewer shows one tab per generation) ----------------------

/** Still waiting for this generation's draft. */
export const isPending = (g) => !!g && IN_FLIGHT_STATUSES.includes(g.status);

/** A finished generation with a report to insert (output "report" or "both"). */
export const hasReport = (g) => !!g && g.status === "completed" && !!g.report_html;

/** A finished generation with a findings list for the LLM report chat (output "findings" or "both"). */
export const hasFindings = (g) => !!g && g.status === "completed" && !!g.findings_html;

/** Its draft arrived and can be read: a report, findings, or both. */
export const isReady = (g) => hasReport(g) || hasFindings(g);

export const isFailed = (g) => !!g && (g.status === "failed" || (g.status === "completed" && !isReady(g)));

/** A ready draft the radiologist has not looked at yet (glows on the button and on its tab). */
export const isUnseen = (g) => isReady(g) && !g.viewed_at;

/** The job behind a generation is not final yet (its model counts as running for this study). */
export const isRunning = (g) => !!g && (g.running ?? isPending(g));

export const runningCount = (generations = []) => generations.filter(isPending).length;

/** Model ids with a generation still running for the study (Regenerate is refused for them). */
export const runningModelIds = (generations = []) =>
  new Set(generations.filter(isRunning).map((g) => g.model_id).filter(Boolean));

/**
 * Tab label per generation id: the model's name, plus "#n" from its second use on
 * ("GPT-5.6 Terra", "Kimi K3", "GPT-5.6 Terra #2").
 */
export const generationLabels = (generations = []) => {
  const seen = {};
  const labels = {};
  generations.forEach((g) => {
    const name = g.model_name || g.model_id || "AI draft";
    const key = g.model_id || name;
    seen[key] = (seen[key] || 0) + 1;
    labels[g.id] = seen[key] > 1 ? `${name} #${seen[key]}` : name;
  });
  return labels;
};

// ---- output: report, findings only, or both ----------------------------------------------------

/** What a generation asks for; findings feed the LLM report chat and are offered only with the LLM report. */
export const AI_DRAFT_OUTPUTS = [
  { key: "report", text: "Report" },
  { key: "findings", text: "Findings only" },
  { key: "both", text: "Report + findings" },
];

/** Tab label suffix for a generation that is not a plain report. */
export const outputSuffix = (g) => (g?.output === "findings" ? " · Findings" : g?.output === "both" ? " · + Findings" : "");

// Lists only: the findings are a <ul>, one finding per <li>
const FINDINGS_TAGS = ["ul", "ol", "li", "p", "strong", "em", "br"];

export const sanitizeFindingsHtml = (html) => DOMPurify.sanitize(html || "", { ALLOWED_TAGS: FINDINGS_TAGS, ALLOWED_ATTR: [] });

/** One line per finding (each <li>, else each paragraph or line), as plain text. */
export const findingsLines = (html) => {
  const div = document.createElement("div");
  div.innerHTML = sanitizeFindingsHtml(html);
  const items = [...div.querySelectorAll("li")];
  const lines = items.length > 0 ? items.map((li) => li.textContent) : htmlToPlainText(div.innerHTML).split("\n");
  return lines.map((line) => (line || "").replace(/\s+/g, " ").trim()).filter(Boolean);
};

const isMentionParagraph = (node) => Array.isArray(node?.children) && node.children.some((c) => c?.type === "mention");

/**
 * Slate content for the LLM report chat input: the paragraph that names the report (the
 * "Create a @<title> report." line with its @mention) followed by one paragraph per finding.
 * Anything else typed in the chat is replaced. When the chat has no @mention yet, `mention`
 * ({ label, value }) builds that line the way the chat's own auto-fill does.
 */
export const findingsChatNodes = (currentNodes, lines, mention = null) => {
  const kept = (Array.isArray(currentNodes) ? currentNodes : []).filter(isMentionParagraph);
  const heading =
    kept.length > 0
      ? kept
      : mention?.label
        ? [
            {
              type: "paragraph",
              children: [
                { text: "Create a " },
                { type: "mention", character: mention.label, value: mention.value ?? null, children: [{ text: "" }] },
                { text: " report." },
              ],
            },
          ]
        : [];
  return [...heading, ...lines.map((line) => ({ type: "paragraph", children: [{ text: line }] }))];
};

/** The @mention ({ label, value }) in Slate content, or null. */
export const mentionOf = (nodes) => {
  for (const node of Array.isArray(nodes) ? nodes : []) {
    const m = (node?.children || []).find((c) => c?.type === "mention");
    if (m) return { label: m.character, value: m.value ?? null };
  }
  return null;
};

/** The tab to open when none is active: the first unseen ready draft, else the latest ready one, else the latest. */
export const pickInitialGeneration = (generations = []) =>
  generations.find(isUnseen) || [...generations].reverse().find(isReady) || generations[generations.length - 1] || null;

/** The AI service's timeout error, in words for the radiologist. */
export const friendlyDraftError = (error) =>
  error ? String(error).replace(/^TimeoutError:\s*no response/i, "No response") : error;

/** Button state from GET /ai-draft/generations ({ enabled, keyImageCount, generations }). */
export const deriveAiDraftState = (data) => {
  if (!data || !data.enabled) return AI_DRAFT_STATE.HIDDEN;
  const generations = Array.isArray(data.generations) ? data.generations : [];
  if (generations.length === 0) return data.keyImageCount > 0 ? AI_DRAFT_STATE.IDLE : AI_DRAFT_STATE.NO_KEY_IMAGES;
  if (generations.some(isUnseen)) return AI_DRAFT_STATE.READY;
  if (generations.some(isPending)) return AI_DRAFT_STATE.GENERATING;
  const latest = generations[generations.length - 1];
  if (isFailed(latest)) return AI_DRAFT_STATE.FAILED;
  const latestReady = [...generations].reverse().find(isReady);
  return latestReady?.stale ? AI_DRAFT_STATE.STALE : AI_DRAFT_STATE.VIEWED;
};

// Same allowlist the backend applies; the draft is sanitised again before it is shown or inserted.
const ALLOWED_TAGS = ["h3", "h4", "p", "strong", "em", "u", "ul", "ol", "li", "br", "table", "thead", "tbody", "tr", "th", "td"];

export const sanitizeDraftHtml = (html) =>
  DOMPurify.sanitize(html || "", { ALLOWED_TAGS, ALLOWED_ATTR: ["colspan", "rowspan"] });

export const htmlToPlainText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html || "";
  div.querySelectorAll("p, h3, h4, li, tr, br").forEach((el) => el.append("\n"));
  return (div.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

// Froala's unlicensed-build branding paragraph; not report content (the submit strips it too)
const FROALA_BRANDING = /<p[^>]*>\s*Powered by\s*<a[^>]*>Froala Editor<\/a>\s*<\/p>/gi;

const withoutBranding = (html) => (html || "").replace(FROALA_BRANDING, "");

export const isReportEmpty = (html) =>
  !html || withoutBranding(html).replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() === ""; // trim() also strips U+00A0

/** Resolves true once condition() holds (e.g. the report panel has loaded a tab), false on timeout. */
export const waitUntil = (condition, timeoutMs = 8000, intervalMs = 100) =>
  new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      if (condition()) return resolve(true);
      if (Date.now() - started >= timeoutMs) return resolve(false);
      setTimeout(check, intervalMs);
    };
    check();
  });

// ---- templates --------------------------------------------------------------------------------

/** Text of report HTML as the reader sees it; Froala reformats HTML, so compare text, not markup. */
export const reportText = (html) => {
  const div = document.createElement("div");
  div.innerHTML = withoutBranding(html).replace(/<[^>]+>/g, " ");
  return (div.textContent || "").replace(/\s+/g, " ").trim(); // \s includes U+00A0
};

const TEMPLATE_BUCKETS = [
  "reportTemplates",
  "rsnaReportTemplates",
  "radiologistReportTemplates",
  "clientReportTemplates",
  "dsrReportTemplates",
];

/**
 * The report template loaded in the active report tab, from the report context:
 * { id, originalHtml }. id is null when the tab is not based on a template; originalHtml is the
 * template as offered by the picker (the tab's own copy is edited in place as the user types).
 */
export const activeReportTemplate = ({ patientReports, activeTabKey, currentActiveTab, allReportTemplates, dropdownSelectedTemplatesStore } = {}) => {
  const tabs = Array.isArray(patientReports) ? patientReports : [];
  const tab =
    tabs.find((p) => activeTabKey != null && p.key === activeTabKey) ||
    tabs.find((p) => currentActiveTab != null && p.text === currentActiveTab);
  const details = tab?.reportDetails;
  const rawId = details?.reportTemplateId ?? details?.id;
  const id = Number(rawId);
  if (!details || !Number.isInteger(id) || id <= 0) return { id: null, originalHtml: null };

  let original = null;
  for (const bucket of TEMPLATE_BUCKETS) {
    const found = (allReportTemplates?.[bucket] || []).find((t) => Number(t?.id) === id);
    if (found) {
      original = found.report ?? found.froalaReport ?? null;
      break;
    }
  }
  if (original == null) {
    const option = (dropdownSelectedTemplatesStore || []).find((o) => Number(o?.key) === id);
    original = option?.reportDetails?.report ?? null;
  }
  return { id, originalHtml: original };
};

/** True when the editor holds nothing but the template exactly as it was loaded. */
export const isUntouchedTemplate = (editorHtml, templateHtml) =>
  templateHtml != null && reportText(templateHtml) !== "" && reportText(editorHtml) === reportText(templateHtml);

// ---- placeholders -----------------------------------------------------------------------------

/** "[ ]", "[ x ]" or a bracketed optional sentence (same rule as the backend sign-time check). */
export const PLACEHOLDER_PATTERN = /\[[^[\]\n]{0,300}\]/g;

/**
 * For display only: wraps every placeholder in <mark class="ai-placeholder"> without touching
 * tags, and counts them. The HTML inserted into the report never contains these marks.
 */
export const highlightPlaceholders = (html) => {
  const container = document.createElement("div");
  container.innerHTML = html || "";
  let count = 0;
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach((node) => {
    const text = node.nodeValue;
    const matches = [...text.matchAll(PLACEHOLDER_PATTERN)];
    if (matches.length === 0) return;
    const fragment = document.createDocumentFragment();
    let last = 0;
    matches.forEach((m) => {
      if (m.index > last) fragment.append(text.slice(last, m.index));
      const mark = document.createElement("mark");
      mark.className = "ai-placeholder";
      mark.textContent = m[0];
      fragment.append(mark);
      last = m.index + m[0].length;
      count += 1;
    });
    if (last < text.length) fragment.append(text.slice(last));
    node.replaceWith(fragment);
  });
  return { html: container.innerHTML, count };
};

/** "Profile: hospital-a · Template: HRCT chest (from editor)" for the panel header. */
export const describeGeneration = (job) => {
  if (!job) return "";
  const info = job.generation_info || {};
  const profile = info.profile_id || job.profile_id;
  let template;
  if (info.template === "request" || (!info.template && job.template_source?.startsWith("editor:"))) {
    template = "from editor";
  } else if (info.template) {
    const name = String(info.template).replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ");
    template = `${name.charAt(0).toUpperCase()}${name.slice(1)} (hospital library)`;
  } else if (job.generation_info) {
    template = "none";
  }
  return [profile && `Profile: ${profile}`, template && `Template: ${template}`].filter(Boolean).join(" · ");
};
